import { Email, InboxThread } from "@tellescope/types-models";
import { TellescopeModelFromName } from "../../mappings";

const DEFAULT_INBOX_STATUS = "New";

/**
 * Helper functions for email processing (from api/modules/inbox.ts)
 */
const URIDecodeEmail = (str: string) => {
  try {
    return decodeURIComponent(str);
  } catch {
    return str;
  }
};

const sanitize_html = (html: string) => {
  // Simple text extraction from HTML - removes tags
  return html.replace(/<[^>]*>/g, "").trim();
};

const get_email_html = (e: Email) =>
  URIDecodeEmail(
    e.htmlEncoding === "base64" && e.HTMLContent && typeof Buffer !== "undefined"
      ? Buffer.from(e.HTMLContent, "base64").toString()
      : e.HTMLContent ?? ""
  );

const get_email_text = (e: Email) =>
  URIDecodeEmail(
    e.textContent && e.textEncoding === "base64" && typeof Buffer !== "undefined"
      ? Buffer.from(e.textContent, "base64").toString()
      : sanitize_html(get_email_html(e) ?? "") || e.textContent
  );

const get_insensitive_subject = (e: Email) =>
  e?.subject?.toLowerCase()?.replaceAll("re:", "")?.replaceAll(" ", "") || "";

export const EXCLUDED_INBOX_THREAD_KEYS_FROM_EMAIL = [
  "id",
  "createdAt",
  "updatedAt",
  "businessId",
  "creator",
  "organizationIds",
  "sharedWithOrganizations",
] as const satisfies (keyof TellescopeModelFromName<"inbox_threads">)[];

type ExcludedInboxThreadKeys = (typeof EXCLUDED_INBOX_THREAD_KEYS_FROM_EMAIL)[number];
type PartialInboxThread = Omit<TellescopeModelFromName<"inbox_threads">, ExcludedInboxThreadKeys>;

export const removeEmailExcludedInboxThreadFields = <T extends Partial<TellescopeModelFromName<"inbox_threads">>>(
  inboxThread: T
): PartialInboxThread => {
  const entries = Object.entries(inboxThread);
  return Object.fromEntries(
    entries.filter(([key]) => !EXCLUDED_INBOX_THREAD_KEYS_FROM_EMAIL.includes(key as ExcludedInboxThreadKeys))
  ) as PartialInboxThread;
};

/**
 * Converts an Email to an InboxThread representation.
 * This follows the same logic as buildEmailThreads in api/modules/inbox.ts
 *
 * Purpose: Used to determine if a new email matches cached inbox_threads
 * getSome filters to decide if the cache should be updated/invalidated.
 *
 * Note: This function handles a single email. The actual buildEmailThreads groups
 * emails by enduserId and subject/threadId. For cache invalidation purposes,
 * this simplified version represents how a single email would appear as a thread.
 *
 * @param email - The email to convert
 * @returns An InboxThread representation of the email
 */
export const convertEmailToInboxThread = (
  email: Email
): Omit<
  InboxThread,
  "id" | "createdAt" | "updatedAt" | "businessId" | "creator" | "organizationIds" | "sharedWithOrganizations"
> => {
  const now = new Date();

  // Don't show automated or bulk email in inbox
  const shouldSkip = email.automationStepId || email.batchId || email.journeyContext?.calendarEventId;
  if (shouldSkip) {
    // Return a minimal thread representation that won't match filters
    return {
      type: "Email",
      title: "",
      preview: "",
      timestamp: new Date(email.timestamp || email.id || new Date()),
      assignedTo: [],
      userIds: [],
      enduserIds: [],
      inboxStatus: DEFAULT_INBOX_STATUS,
      threadId: email.id,
    };
  }

  // Determine if this is a draft or scheduled message
  const isDraft = email.isDraft;
  const isScheduled = !isDraft && email.sendAt && new Date(email.sendAt) > now;

  // For drafts and scheduled messages, we still create thread representation
  const useForPreview = !isDraft && !isScheduled;

  return {
    type: "Email",
    title: email.subject || "",
    preview: useForPreview ? get_email_text(email).substring(0, 100) : "",
    timestamp: new Date(email.timestamp || email.id || new Date()),
    assignedTo: email.assignedTo || [],
    userIds: email.inbound && email.userId ? [email.userId] : [],
    enduserIds: email.enduserId ? [email.enduserId] : [],
    inboxStatus: email.inbound ? email.inboxStatus || DEFAULT_INBOX_STATUS : DEFAULT_INBOX_STATUS,
    threadId: get_insensitive_subject(email) || email.id,
    tags: email.inbound ? email.tags || [] : [],
    readBy: email.inbound ? email.readBy || {} : {},
    emailMessageId: email.messageId || null,
    outboundPreview: !email.inbound ? get_email_text(email).substring(0, 100) : "",
    outboundTimestamp: !email.inbound ? new Date(email.timestamp || email.id || new Date()) : undefined,
    ...(email.userId && !email.inbound ? { recentOutboundUserId: email.userId } : {}),
    ...(email.enduserId && email.inbound ? { recentInboundEnduserId: email.enduserId } : {}),
    archivedAt: email.archivedAt || "",
    trashedAt: email.trashedAt || "",
    draftMessageIds: isDraft ? [email.id] : [],
    scheduledMessageIds: isScheduled ? [email.id] : [],
  };
};
